import React, { useEffect } from 'react';
import { ADMIN_PATH, PortalProvider, STAFF_PATH, STAFF_RESET_PATH, STAFF_SETUP_PATH, usePortal } from './contexts/PortalContext';
import { Login } from './pages/Login';
import { StaffLogin } from './pages/StaffLogin';
import { StaffSetupPassword } from './pages/StaffSetupPassword';
import { ClientApp } from './pages/ClientApp';
import { PortalApp } from './pages/PortalApp';
import { ViewToggle } from './components/ViewToggle';
import { PreviewBanner } from './components/PreviewBanner';
import { Toast } from './components/Toast';
import { CLIENT_ACCESS_TITLE, STAFF_TITLE, projectTitle, setPageTitle } from './utils/pageTitle';
function Portal() {
  const {
    path,
    navigate,
    staffAuthed,
    staffChecking,
    authed,
    previewAsClient,
    activeProject
  } = usePortal();
  const onAdmin = path.startsWith(ADMIN_PATH);
  /* Checked first: an invitation link must never fall through to sign-in. */
  const onStaffSetup = path.startsWith(STAFF_SETUP_PATH) || path.startsWith(STAFF_RESET_PATH);
  const onStaffLogin = !onStaffSetup && path.startsWith(STAFF_PATH);

  /* Staff pages carry the Portal name; a client tab carries their own, taken
     from the stored client_name once the project has loaded. */
  useEffect(() => {
    const staffSide = onAdmin && !previewAsClient || onStaffLogin || onStaffSetup;
    if (staffSide) {
      setPageTitle(STAFF_TITLE);
      return;
    }
    if (!authed && !previewAsClient) {
      setPageTitle(CLIENT_ACCESS_TITLE);
      return;
    }
    setPageTitle(projectTitle(activeProject.client));
  }, [onAdmin, onStaffLogin, onStaffSetup, previewAsClient, authed, activeProject.client]);

  // /admin is protected: without a verified Supabase staff session you are sent
  // to /staff, and knowing the URL is never enough. Signed in, /staff hands you
  // on to /admin. Both wait for the persisted session to be checked first.
  useEffect(() => {
    if (staffChecking) return;
    if (onAdmin && !staffAuthed) navigate(STAFF_PATH);
    if (onStaffLogin && staffAuthed) navigate(ADMIN_PATH);
  }, [onAdmin, onStaffLogin, staffAuthed, staffChecking, navigate]);

  /* An invitation link: the session it carries is read here, so no redirect
     and no session check runs first. */
  if (onStaffSetup) {
    return <>
        <StaffSetupPassword data-id="element-0" data-mp="85l0op2u8u85" />
        <Toast data-id="element-1" data-mp="86mf93j2byum" />
      </>;
  }
  if (onAdmin) {
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-2" data-mp="3gl4pnbwh7qhe" />;
    if (!staffAuthed) {
      return <>
          <StaffLogin data-id="element-3" data-mp="3bmthr6pqrq1t" />
          <Toast data-id="element-4" data-mp="3bnmh3mmdfd9x" />
        </>;
    }
    if (previewAsClient) {
      return <>
          <PreviewBanner data-id="element-5" data-mp="3qbzfsrcufa84" />
          <ClientApp data-id="element-6" data-mp="3qcipjkscasb2" />
          <Toast data-id="element-7" data-mp="3lkhdnzz333lk" />
        </>;
    }
    return <>
        <PortalApp data-id="element-8" data-mp="3liwgs3par8kj" />
        <ViewToggle data-id="element-9" data-mp="3limrkic2t5fw" />
        <Toast data-id="element-10" data-mp="2xun4bsdz3x62" />
      </>;
  }
  if (onStaffLogin) {
    /* Nothing renders until the persisted session has been checked, so an
       already-signed-in staff member never sees the login form flash. */
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-11" data-mp="18cq4thhcsarc" />;
    return <>
        <StaffLogin data-id="element-12" data-mp="1d4zt6t2tl2ri" />
        <ViewToggle data-id="element-13" data-mp="1d5j9k5ctnj3f" />
        <Toast data-id="element-14" data-mp="1d62l94bvpb90" />
      </>;
  }
  if (!authed) {
    return <>
        <Login data-id="element-15" data-mp="ygmtpm4ix1s9" />
        <ViewToggle data-id="element-16" data-mp="yh6f5ctdu8b1" />
      </>;
  }
  return <>
      <ClientApp data-id="element-17" data-mp="13e56xopkq362" />
      <ViewToggle data-id="element-18" data-mp="13fpyr6ki7808" />
      <Toast data-id="element-19" data-mp="13f7t5jbybhfn" />
    </>;
}
export function App() {
  return <PortalProvider data-id="element-20" data-mp="1r3zhfzmf7fn5">
      <Portal data-id="element-21" data-mp="1r5kcrrgk72m2" />
    </PortalProvider>;
}